import { AxiosError } from "axios";
import currency from "currency.js";
import React, {
  ReactElement,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
} from "react";
import useSWR from "swr";
import { GetAccount200Response } from "../../codegen-api";
import { AuthContext } from "../AuthContext";
import { WebsocketContext } from "../WebsocketAuthContext";
import { APIEndpointEnum } from "../../enums/endpoint";
import { authApi } from "../../services/api/apiFetcher";
import { pollingInterval } from "../../services/api/pollingInterval";
import { debounceRevalidation } from "../../utils/revalidation";
import {
  getRealtimeAccount,
  getRealtimeEventId,
  getRealtimeSnapshotSignature,
  isMeaningfulAccountRealtimeEvent,
  parseRealtimeMessage,
} from "../../utils/realtimeRevalidation";
import { forEachFrame } from "../../hooks/wss/parseFrame";
import { useLoggerMiddleware } from "../../hooks/api/middleware/useLoggerMiddleware";
import { useAccountKeepPreviousData } from "../../hooks/api/useAccountKeepPreviousData";
import { isStaleApiCredentialError } from "../../utils/authError";
import {
  accountSignature,
  useTradeSyncTarget,
} from "../../services/tradeSync";
import { IAccountContextType } from "./types";
import { isAccountSnapshot, normalizeAccountData } from "./helpers";
import { useAccountMutations } from "./useAccountMutations";

export const AccountContext = React.createContext<
  IAccountContextType | undefined
>(undefined);

interface IAccountContextProviderProps {
  children: ReactElement;
}

// Single source of truth for the `/account` endpoint.
//
// This provider owns the ONE SWR subscription, websocket-driven revalidation,
// polling and account mutations. Every consumer reads the shared value through
// `useGetAccount()` (a thin `useContext` reader) instead of spinning up its own
// fetch driver — which previously caused 40+ parallel `/account` requests on
// load.
export function AccountContextProvider({
  children,
}: IAccountContextProviderProps) {
  // Get API Key
  const { apiKey, apiSecret, account, deleteAuthInfo } = useContext(AuthContext);
  const {
    authenticated,
    triggerSubscribe,
    triggerUnsubscribe,
    registerRevalidate,
    lastMessages,
  } = useContext(WebsocketContext);
  const fetcher = authApi(apiKey, apiSecret);
  const processedRealtimeEvents = useRef<Set<string>>(new Set());
  const lastRealtimeSnapshots = useRef<Map<string, string>>(new Map());
  const accountKey = account?.toLowerCase();
  const { keepPreviousData, trackData } = useAccountKeepPreviousData(accountKey);

  const swr = useSWR<GetAccount200Response, Error>(
    // If apiKey is not available, the request will not fetch
    apiKey && apiSecret && accountKey
      ? [APIEndpointEnum.ACCOUNT, accountKey, apiKey, apiSecret]
      : undefined,
    async () => {
      const accountData = (await (await fetcher.getAccount())()).data;
      if (accountData?.signing_keys?.length === 0) {
        deleteAuthInfo("signing");
      }
      return normalizeAccountData(accountData);
    },
    {
      use: [useLoggerMiddleware],
      revalidateIfStale: true,
      revalidateOnMount: true,
      shouldRetryOnError: true,
      errorRetryCount: 3,
      errorRetryInterval: 1000,
      // Only while SWR's last data is this same account's (FE-14): a wallet
      // switch shows loading, never the previous account's balance.
      keepPreviousData,
      dedupingInterval: 5000,
      focusThrottleInterval: 10000,
      refreshInterval: pollingInterval[APIEndpointEnum.ACCOUNT],
      revalidateOnFocus: false,
      onError: (e) => {
        const error = e as AxiosError;
        const isInvalidToken =
          //@ts-ignore
          error.response?.data?.trim?.() === "INVALID_TOKEN";
        if (isInvalidToken || isStaleApiCredentialError(e)) {
          deleteAuthInfo("api");
          swr.mutate(undefined, { revalidate: false });
        }
      },
    }
  );
  trackData(swr.data);

  const accountRealtimeChannels = useMemo(
    () =>
      account
        ? [
            `account:${account.toLowerCase()}`,
          ]
        : [],
    [account]
  );

  // Set below, once `revalidateAccountSoon` exists; run when the server could
  // not send the account snapshot or frames were dropped.
  const revalidateOnResyncRef = useRef<() => void>(() => {});

  useEffect(() => {
    if (!authenticated || accountRealtimeChannels.length === 0) {
      return undefined;
    }
    accountRealtimeChannels.forEach((channel) => {
      triggerSubscribe("subscribe", [channel]);
    });
    const unregister = registerRevalidate?.(accountRealtimeChannels, () =>
      revalidateOnResyncRef.current()
    );
    // Release on unmount, account change or de-auth (M1).
    return () => {
      unregister?.();
      triggerUnsubscribe(accountRealtimeChannels);
    };
  }, [
    accountRealtimeChannels,
    authenticated,
    registerRevalidate,
    triggerSubscribe,
    triggerUnsubscribe,
  ]);

  // One trailing-edge `/account` refetch per burst of realtime events (FE-9).
  // This used to fire an immediate fetch plus three more (500/1500/4000 ms)
  // for every event that passed a 1 s gate.
  const revalidateAccountSoon = useCallback(() => {
    if (!accountKey) return;
    debounceRevalidation(`${APIEndpointEnum.ACCOUNT}:${accountKey}`, () => {
      void swr.mutate();
    });
  }, [accountKey, swr.mutate]);
  revalidateOnResyncRef.current = revalidateAccountSoon;

  // ── Post-trade convergence for `/account` ───────────────────────────────
  // `/account` carries the balances and margin a trader watches immediately
  // after trading, so it converges on its own evidence instead of riding the
  // broad post-trade sweep: refetch until the payload actually changes, then
  // stop. That is one request when the backend is quick, against the five the
  // sweep spent unconditionally.
  // This used to hand-roll the run so it could publish nothing; it now goes
  // through the shared hook, which reports SYNCING / LIVE / STALE like every
  // other convergence owner. One implementation, one status contract.
  useTradeSyncTarget({
    targetId: `account:${accountKey || "anon"}`,
    enabled: Boolean(accountKey),
    current: () => swr.data,
    refetch: () => swr.mutate(),
    signature: accountSignature,
  });

  useEffect(() => {
    if (!lastMessages || accountRealtimeChannels.length === 0) {
      return;
    }

    const channelSet = new Set(accountRealtimeChannels.map((c) => c.toLowerCase()));
    const normalizedAccount = account?.toLowerCase();
    let shouldRevalidate = false;

    forEachFrame(lastMessages, "AccountContextProvider", (lastMessage) => {
      const parsed = parseRealtimeMessage(lastMessage.data);
      const channel = parsed?.channel?.toLowerCase();
      const isBackendAccountEvent =
        normalizedAccount &&
        getRealtimeAccount(parsed) === normalizedAccount &&
        channel === "account";
      if (!channel || (!channelSet.has(channel) && !isBackendAccountEvent)) {
        return;
      }

      if (isBackendAccountEvent && isAccountSnapshot(parsed?.data)) {
        swr.mutate(normalizeAccountData(parsed.data), { revalidate: false });
        return;
      }
      if (isBackendAccountEvent) {
        shouldRevalidate = true;
        return;
      }

      const snapshotSignature = getRealtimeSnapshotSignature(parsed, channel);
      const snapshotKey = `${channel}:${normalizedAccount}`;
      let eventId = getRealtimeEventId(parsed, channel);
      if (snapshotSignature !== undefined) {
        const previousSignature = lastRealtimeSnapshots.current.get(snapshotKey);
        lastRealtimeSnapshots.current.set(snapshotKey, snapshotSignature);
        if (
          previousSignature === undefined ||
          previousSignature === snapshotSignature
        ) {
          return;
        }
        eventId = `${snapshotKey}:${snapshotSignature}`;
      } else if (!isMeaningfulAccountRealtimeEvent(parsed, channel)) {
        return;
      }

      if (processedRealtimeEvents.current.has(eventId)) {
        return;
      }

      processedRealtimeEvents.current.add(eventId);
      if (processedRealtimeEvents.current.size > 200) {
        const oldest = processedRealtimeEvents.current.values().next().value;
        if (oldest) processedRealtimeEvents.current.delete(oldest);
      }
      shouldRevalidate = true;
    });

    if (shouldRevalidate) {
      revalidateAccountSoon();
    }
  }, [account, accountKey, accountRealtimeChannels, lastMessages, revalidateAccountSoon]);

  const showOnboarding = useMemo(() => {
    if (swr.data) {
      // A null equity is unknown (a position is unpriced), not an empty
      // account: `currency(null)` is 0 and sent a funded trader to onboarding.
      if ((swr.data.equity as unknown) === null) {
        return false;
      }
      return (
        currency(swr.data.available_balance).value === 0 &&
        currency(swr.data.equity).value === 0
      );
    }
    return undefined;
  }, [swr.data]);

  const { removeApiKey, removeSigningKey, addApiKey, updateIsolatedMargin } =
    useAccountMutations(fetcher, swr);

  // When there is no live account (disconnected / missing creds) expose NO
  // account data. `keepPreviousData: true` (see the SWR options above) otherwise
  // retains the last account's balance after the key clears, so every balance
  // readout (Portfolio, "Available to Trade", Assets) would keep showing stale
  // values after disconnect. Gating on the SAME condition as the SWR key makes
  // this the single, reliable clear-point for all consumers — no stale fallback.
  const hasLiveAccount = Boolean(apiKey && apiSecret && accountKey);

  const value = useMemo<IAccountContextType>(
    () => ({
      ...swr,
      data: hasLiveAccount ? swr.data : undefined,
      removeApiKey,
      removeSigningKey,
      addApiKey,
      updateIsolatedMargin,
      showOnboarding,
    }),
    [
      swr,
      hasLiveAccount,
      removeApiKey,
      removeSigningKey,
      addApiKey,
      updateIsolatedMargin,
      showOnboarding,
    ]
  );

  return (
    <AccountContext.Provider value={value}>{children}</AccountContext.Provider>
  );
}
